import * as React from "react";
import { useState, useRef, useEffect } from "react";
import { RecaptchaVerifier, signInWithPhoneNumber } from "firebase/auth";
import { themes } from "services/constants";
import { useForm } from "react-hook-form";
import * as Mui from "@mui/material";
import { InputAdornment } from "@material-ui/core";
import {
  Mail,
  Phone,
  PersonOutlined,
  ArrowBackOutlined,
  Check,
  InfoOutlined,
} from "@mui/icons-material";
import { showToast } from "services/utils/Status";
import { useSelector } from "react-redux";
import sessionHandling from "services/utils/notificationUtils";
import {
  useHistory,
  useLocation,
} from "react-router-dom/cjs/react-router-dom.min";
import { auth } from "../../../firebase";
import "../Login/Login.css";
import {
  tokens,
  headingSx,
  fieldLabelSx,
  inputRootSx,
  primaryButtonSx,
  secondaryButtonSx,
  stepPillSx,
} from "../Login/brandTokens";
import {
  AuthLayout,
  AuthLeftPanel,
  AuthRightPanel,
  AuthCard,
} from "../Login/AuthShell";
import {
  parseJsonSafely,
  getBackendErrorMessage,
  errorMessageFor,
  BackendRequestError,
} from "../Login/authErrors";

const COUNTRY_CODES = [
  { label: "🇮🇳 +91", value: "+91" },
  { label: "🇸🇬 +65", value: "+65" },
  { label: "🇬🇧 +44", value: "+44" },
  { label: "🇦🇪 +971", value: "+971" },
  { label: "🇺🇸 +1", value: "+1" },
  { label: "🇩🇪 +49", value: "+49" },
  { label: "🇲🇾 +60", value: "+60" },
  { label: "🇦🇺 +61", value: "+61" },
];

const GENERIC_SEND_ERROR =
  "Couldn't send the verification code. Please try again.";

const detectIdentifierType = (value) => {
  const trimmed = value.trim();
  if (!trimmed) return null;
  if (trimmed.includes("@")) return "email";
  const digits = trimmed.replace(/[\s+()-]/g, "");
  if (/^\d{5,}$/.test(digits) || trimmed.startsWith("+")) return "phone";
  return null;
};

export const SendOTP = () => {
  const history = useHistory();
  const location = useLocation();
  const { domain } = useSelector((state) => state.tokenReducer);
  const [isLoading, setIsLoading] = useState(false);
  const [sendOTP, setSendOTP] = useState(false);
  const [identifier, setIdentifier] = useState("");
  const [identifierType, setIdentifierType] = useState(null);
  const [countryCode, setCountryCode] = useState("+91");
  const [isTimerActive, setIsTimerActive] = useState(false);
  const [secondsRemaining, setSecondsRemaining] = useState(90);
  const [otpVerification, setOtpVerification] = useState(false);
  const [confirmationResult, setConfirmationResult] = useState(null);
  const [otp, setOtp] = useState(["", "", "", "", "", ""]);
  const inputRefs = useRef([]);
  const recaptchaVerifierRef = useRef(null);
  const { handleSubmit, reset } = useForm();
  const isForgotPassword =
    localStorage.getItem("PasswordHandling") === "ForgotPassword";
  const pageTitle = isForgotPassword
    ? "Reset your password"
    : "Create password";
  const actionLabel = isForgotPassword ? "reset" : "create";
  const stepItems = [
    {
      number: 1,
      title: "Verify your identity",
      subtitle: "Email or mobile - we detect it",
    },
    {
      number: 2,
      title: "Enter the OTP",
      subtitle: "Check your inbox or SMS",
    },
    {
      number: 3,
      title: "Set a new password",
      subtitle: "Choose something strong",
    },
  ];

  const maskIdentifier = React.useCallback((value, type) => {
    if (!value) return "";
    if (type === "email" && value.includes("@")) {
      const [name, host] = value.split("@");
      const prefix = name.slice(0, 2);
      return `${prefix}${"*".repeat(Math.max(name.length - 2, 3))}@${host}`;
    }
    if (type === "phone") {
      const visible = value.slice(-4);
      return `******${visible}`;
    }
    return value;
  }, []);

  const clearRecaptchaVerifier = React.useCallback(() => {
    recaptchaVerifierRef.current?.clear();
    recaptchaVerifierRef.current = null;

    const container = document.getElementById("recaptcha-container");
    if (container) {
      container.replaceChildren();
    }
  }, []);

  const createRecaptchaVerifier = React.useCallback(() => {
    clearRecaptchaVerifier();
    recaptchaVerifierRef.current = new RecaptchaVerifier(
      auth,
      "recaptcha-container",
      {
        size: "invisible",
        "expired-callback": clearRecaptchaVerifier,
      },
    );
    return recaptchaVerifierRef.current;
  }, [clearRecaptchaVerifier]);

  const handleIdentifierChange = (e) => {
    const val = e.target.value;
    setIdentifier(val);
    setIdentifierType(detectIdentifierType(val));
  };

  const getLocalPhone = () => {
    const digitsOnly = identifier.trim().replace(/[\s()-]/g, "");
    const e164 = digitsOnly.startsWith("+")
      ? digitsOnly
      : countryCode + digitsOnly;
    return e164.substring(countryCode.length);
  };

  useEffect(() => clearRecaptchaVerifier, [clearRecaptchaVerifier]);

  useEffect(() => {
    if (location.state?.initialStep === "otp") {
      const restoredIdentifier = location.state.identifier || "";
      const restoredType =
        location.state.identifierType ||
        detectIdentifierType(restoredIdentifier);
      setIdentifier(restoredIdentifier);
      setIdentifierType(restoredType);
      setSendOTP(true);
      setOtp(["", "", "", "", "", ""]);
      setOtpVerification(false);
      setIsTimerActive(false);
      setSecondsRemaining(90);
    }
  }, [location.state]);

  useEffect(() => {
    let timer;
    if (isTimerActive && secondsRemaining > 0) {
      timer = setInterval(() => {
        setSecondsRemaining((prevSeconds) => prevSeconds - 1);
      }, 1000);
    }
    if (secondsRemaining === 0) {
      setIsTimerActive(false);
      setSecondsRemaining(90);
    }
    return () => clearInterval(timer);
  }, [isTimerActive, secondsRemaining]);

  const mobileNumberWithSendOTP = (error) => {
    switch (error) {
      case "Firebase: Error (auth/invalid-app-credential).":
        showToast(
          "error",
          "SMS verification is currently unavailable. Please use email instead.",
        );
        break;
      case "Firebase: Error (auth/too-many-requests).":
        showToast(
          "error",
          "Too many attempts on this mobile number. Please wait and try again.",
        );
        break;
      case "Firebase: Error (auth/code-expired).":
        showToast("error", "Your code has expired. Please request a new one.");
        break;
      case "Firebase: Error (auth/invalid-verification-code).":
        showToast("error", "Incorrect code. Please check and try again.");
        break;
      case "Firebase: Invalid format. (auth/invalid-phone-number).":
        showToast("error", "Please check your mobile number and try again.");
        break;
      case "Firebase: Hostname match not found (auth/captcha-check-failed).":
        showToast(
          "error",
          "This site is not set up for mobile verification. Please contact your administrator.",
        );
        break;
      default:
        showToast("error", GENERIC_SEND_ERROR);
        break;
    }
  };

  const onSubmit = async () => {
    setOtp(["", "", "", "", "", ""]);

    if (!identifier.trim()) {
      showToast("error", "Please enter your email or mobile number.");
      return;
    }
    if (!identifierType) {
      showToast(
        "error",
        "Please enter a valid email address or mobile number.",
      );
      return;
    }

    const trimmedId = identifier.trim();

    if (identifierType === "email") {
      setIsTimerActive(true);
      setIsLoading(true);
      try {
        const response = await fetch(
          `${domain}email/send/otp/?email=${trimmedId}`,
          {
            method: "GET",
            headers: { "Content-Type": "application/json" },
          },
        );
        const res = await parseJsonSafely(response);
        if (response.ok) {
          showToast("success", "Code sent to your email address.");
          setSendOTP(true);
          setOtp(["", "", "", "", "", ""]);
          setOtpVerification(false);
          setIsLoading(false);
        } else if (response.status === 409) {
          setIsTimerActive(false);
          setIsLoading(false);
          sessionHandling();
        } else {
          throw new BackendRequestError(
            getBackendErrorMessage(res) || GENERIC_SEND_ERROR,
          );
        }
      } catch (error) {
        console.error('[SendOTP] send email OTP failed:', error);
        setIsTimerActive(false);
        setIsLoading(false);
        showToast(
          "error",
          error instanceof BackendRequestError
            ? error.message
            : GENERIC_SEND_ERROR,
        );
      }
    } else if (identifierType === "phone") {
      const digitsOnly = trimmedId.replace(/[\s()-]/g, "");
      const phoneE164 = digitsOnly.startsWith("+")
        ? digitsOnly
        : countryCode + digitsOnly;
      const localPhone = phoneE164.substring(countryCode.length);

      setIsLoading(true);
      try {
        const response = await fetch(
          `${domain}unique/phone-number/?phone_no=${localPhone}`,
          {
            method: "GET",
            headers: { "Content-Type": "application/json" },
          },
        );
        const resp = await parseJsonSafely(response);
        if (!response.ok) {
          showToast("error", errorMessageFor(resp, GENERIC_SEND_ERROR));
        } else if (resp.exists) {
          const appVerifier = createRecaptchaVerifier();
          const result = await signInWithPhoneNumber(
            auth,
            phoneE164,
            appVerifier,
          );
          setConfirmationResult(result);
          setIsTimerActive(true);
          showToast("success", "Code sent to your mobile number.");
          setSendOTP(true);
          setOtp(["", "", "", "", "", ""]);
          setOtpVerification(false);
        } else {
          showToast(
            "error",
            errorMessageFor(
              resp,
              "Mobile number not found. Please contact your administrator.",
            ),
          );
        }
      } catch (error) {
        console.error("Error sending OTP:", error);
        mobileNumberWithSendOTP(error.message);
      } finally {
        clearRecaptchaVerifier();
        setIsLoading(false);
      }
    }
  };

  const handleChange = (e, index) => {
    const value = e.target.value;
    if (/^[0-9]$/.test(value)) {
      const newOtp = [...otp];
      newOtp[index] = value;
      setOtp(newOtp);
      if (index < otp.length - 1) {
        inputRefs.current[index + 1].focus();
      }
    }
  };

  const handleKeyDown = (e, index) => {
    if (e.key === "Backspace") {
      const newOtp = [...otp];
      newOtp[index] = "";
      setOtp(newOtp);
      if (otp[index] === "") {
        if (index > 0) {
          inputRefs.current[index - 1].focus();
        }
      }
    }
  };

  const verifyOTP = async () => {
    let otpNumber = otp[0] + otp[1] + otp[2] + otp[3] + otp[4] + otp[5];
    if (otpNumber.length === 6 && identifierType !== "phone") {
      try {
        function getCSRFToken() {
          return document.cookie
            .split("; ")
            .find((row) => row.startsWith("csrftoken="))
            ?.split("=")[1];
        }

        const isLocalhost =
          window.location.hostname === "localhost" ||
          window.location.hostname.startsWith("127.");

        let response;
        if (!isLocalhost) {
          await fetch(`${domain}csrf/`, { credentials: "include" });
          response = await fetch(
            `${domain}email/send/otp/?email=${identifier}&otp=${otpNumber}`,
            {
              method: "PUT",
              credentials: "include",
              headers: {
                "Content-Type": "application/json",
                "X-CSRFToken": getCSRFToken(),
              },
            },
          );
        } else {
          response = await fetch(
            `${domain}email/send/otp/?email=${identifier}&otp=${otpNumber}`,
            {
              method: "PUT",
              headers: { "Content-Type": "application/json" },
            },
          );
        }
        const res = await parseJsonSafely(response);
        if (response.ok) {
          showToast(
            "success",
            "Code verified. You can now set your new password.",
          );
          localStorage.setItem("resetPasswordLink", res.reset_password_url);
          history.push("/auth/otp/newPassword", {
            identifier,
            identifierType,
          });
          reset();
        } else if (response.status === 409) {
          sessionHandling();
        } else {
          throw new BackendRequestError(
            getBackendErrorMessage(res) || GENERIC_SEND_ERROR,
          );
        }
      } catch (error) {
        console.error('[SendOTP] verify email OTP failed:', error);
        setOtpVerification(false);
        showToast(
          "error",
          error instanceof BackendRequestError
            ? error.message
            : GENERIC_SEND_ERROR,
        );
      }
    } else if (otpNumber.length === 6 && identifierType === "phone") {
      const localPhone = getLocalPhone();
      if (confirmationResult) {
        confirmationResult
          .confirm(otpNumber)
          .then(() => {
            generateLinkToResetPassword(
              `${domain}email/send/otp/?phone_no=${localPhone}&otp=${otpNumber}`,
            );
          })
          .catch((error) => {
            mobileNumberWithSendOTP(error.message);
          });
      }
    } else {
      setOtpVerification(true);
    }
  };

  const generateLinkToResetPassword = async (url) => {
    try {
      function getCSRFToken() {
        return document.cookie
          .split("; ")
          .find((row) => row.startsWith("csrftoken="))
          ?.split("=")[1];
      }

      const isLocalhost =
        window.location.hostname === "localhost" ||
        window.location.hostname.startsWith("127.");

      let response;

      if (!isLocalhost) {
        await fetch(`${domain}csrf/`, { credentials: "include" });

        response = await fetch(url, {
          method: "PUT",
          credentials: "include",
          headers: {
            "Content-Type": "application/json",
            "X-CSRFToken": getCSRFToken(),
          },
        });
      } else {
        response = await fetch(url, {
          method: "PUT",
          headers: { "Content-Type": "application/json" },
        });
      }
      const res = await parseJsonSafely(response);
      if (response.ok) {
        showToast("success", "Verification complete.");
        localStorage.setItem("resetPasswordLink", res.reset_password_url);
        history.push("/auth/otp/newPassword", {
          identifier,
          identifierType,
        });
        reset();
      } else if (response.status === 409) {
        sessionHandling();
      } else {
        throw new BackendRequestError(
          getBackendErrorMessage(res) || GENERIC_SEND_ERROR,
        );
      }
    } catch (error) {
      console.error('[SendOTP] reset link request failed:', error);
      setOtpVerification(false);
      showToast(
        "error",
        error instanceof BackendRequestError
          ? error.message
          : GENERIC_SEND_ERROR,
      );
    }
  };

  return (
    <React.Fragment>
      <AuthLayout>
        <AuthLeftPanel
          top={
            <Mui.Stack spacing={{ md: 4, lg: 5 }}>
              <Mui.Box sx={{ maxWidth: 520 }}>
                <Mui.Typography
                  sx={{
                    fontSize: { md: 28, lg: 40, xl: 46 },
                    fontWeight: 700,
                    lineHeight: 1.15,
                    letterSpacing: "-0.02em",
                  }}
                >
                  Regain access in
                  <br />
                  three simple steps
                </Mui.Typography>
                <Mui.Typography
                  sx={{
                    mt: 2.5,
                    fontSize: { md: 15, lg: 17 },
                    lineHeight: 1.5,
                    color: "rgba(255,255,255,0.72)",
                    maxWidth: 460,
                  }}
                >
                  We&apos;ll verify your identity and get you back in
                  securely.
                </Mui.Typography>
              </Mui.Box>

              <Mui.Stack spacing={0} sx={{ maxWidth: 460 }}>
                {stepItems.map((item, index) => {
                  const currentStep = sendOTP ? 2 : 1;
                  const isActive = item.number === currentStep;
                  const isCompleted = item.number < currentStep;

                  return (
                    <Mui.Box
                      key={item.number}
                      sx={{
                        display: "flex",
                        alignItems: "flex-start",
                        gap: 2.5,
                        position: "relative",
                      }}
                    >
                      <Mui.Box sx={{ position: "relative", flexShrink: 0 }}>
                        {index < stepItems.length - 1 ? (
                          <Mui.Box
                            sx={{
                              position: "absolute",
                              top: 54,
                              left: "50%",
                              transform: "translateX(-50%)",
                              width: 0,
                              height: 24,
                              borderLeft: "2px dashed rgba(255,255,255,0.25)",
                            }}
                          />
                        ) : null}
                        <Mui.Box
                          sx={{
                            width: 54,
                            height: 54,
                            borderRadius: "50%",
                            border: isActive
                              ? `2px solid ${tokens.mint}`
                              : isCompleted
                                ? `2px solid ${tokens.mint}`
                                : "2px solid rgba(255,255,255,0.30)",
                            backgroundColor: isActive
                              ? tokens.mint
                              : isCompleted
                                ? tokens.mintTint
                                : "rgba(255,255,255,0.08)",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            fontSize: 17,
                            fontWeight: 600,
                            color: isActive
                              ? tokens.mintDark
                              : isCompleted
                                ? tokens.mint
                                : "rgba(255,255,255,0.95)",
                          }}
                        >
                          {isCompleted ? (
                            <Check fontSize="small" />
                          ) : (
                            item.number
                          )}
                        </Mui.Box>
                      </Mui.Box>
                      <Mui.Box sx={{ pt: 0.75, pb: 3 }}>
                        <Mui.Typography
                          sx={{
                            fontSize: 17,
                            fontWeight: isActive ? 700 : 500,
                            color: isActive ? "#fff" : "rgba(255,255,255,0.92)",
                          }}
                        >
                          {item.title}
                        </Mui.Typography>
                        <Mui.Typography
                          sx={{
                            mt: 0.5,
                            fontSize: 14.5,
                            color: isActive
                              ? "rgba(255,255,255,0.62)"
                              : "rgba(255,255,255,0.52)",
                          }}
                        >
                          {item.subtitle}
                        </Mui.Typography>
                      </Mui.Box>
                    </Mui.Box>
                  );
                })}
              </Mui.Stack>
            </Mui.Stack>
          }
        />

        <AuthRightPanel>
          <form
            onSubmit={handleSubmit(onSubmit)}
            style={{ width: "100%", display: "flex", justifyContent: "center" }}
          >
            <AuthCard maxWidth={{ xs: "100%", sm: 520, md: 480, lg: 560, xl: 600 }}>
              <Mui.Box
                sx={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  gap: 2,
                  mb: 3,
                }}
              >
                <Mui.Button
                  variant="outlined"
                  onClick={() => {
                    if (sendOTP) {
                      setSendOTP(false);
                      setOtp(["", "", "", "", "", ""]);
                      setOtpVerification(false);
                    } else {
                      history.push("/auth/login");
                    }
                  }}
                  startIcon={<ArrowBackOutlined />}
                  sx={{
                    ...secondaryButtonSx,
                    width: "fit-content",
                    justifyContent: "flex-start",
                    minHeight: 0,
                    height: 44,
                    px: 2,
                    fontSize: 14.5,
                  }}
                >
                  {sendOTP ? "Back" : "Back to sign in"}
                </Mui.Button>

                <Mui.Box sx={stepPillSx}>
                  {sendOTP ? "Step 2 of 3" : "Step 1 of 3"}
                </Mui.Box>
              </Mui.Box>

              <Mui.Typography sx={headingSx}>
                {sendOTP ? "Enter the code" : pageTitle}
              </Mui.Typography>

              <Mui.Typography
                sx={{
                  mt: 1,
                  fontSize: 15,
                  lineHeight: 1.4,
                  color: "rgba(21,23,26,0.7)",
                }}
              >
                {sendOTP
                  ? `A 6-digit code was sent via ${
                      identifierType === "email" ? "email to" : "SMS to"
                    } ${maskIdentifier(identifier, identifierType)}. Enter it below.`
                  : `Enter your email address or mobile number and we'll send you a ${actionLabel} code.`}
              </Mui.Typography>

              {!sendOTP ? (
                <Mui.Box sx={{ mt: 3.5, width: "100%" }}>
                  <Mui.Typography sx={fieldLabelSx}>
                    Email or mobile number
                  </Mui.Typography>
                  <Mui.TextField
                    sx={{
                      width: "100%",
                      "& .MuiOutlinedInput-root": inputRootSx,
                      "& .MuiOutlinedInput-input": {
                        py: 1.75,
                        pr: 1.5,
                      },
                      "& input:-webkit-autofill": {
                        WebkitBoxShadow: "0 0 0 100px #fff inset",
                        WebkitTextFillColor: "#15171a",
                        caretColor: "#15171a",
                        borderRadius: "inherit",
                      },
                    }}
                    size="medium"
                    type="text"
                    placeholder="you@company.com or +91 98765 43210"
                    value={identifier}
                    onChange={handleIdentifierChange}
                    helperText="We'll auto-detect whether this is an email or phone number."
                    FormHelperTextProps={{
                      sx: {
                        color: "rgba(21,23,26,0.52)",
                        fontSize: 13,
                        mt: 1,
                        ml: 0.5,
                      },
                    }}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          {identifierType === "phone" ? (
                            <React.Fragment>
                              <Phone color="primary" sx={{ mr: 0.5 }} />
                              <Mui.Select
                                value={countryCode}
                                onChange={(e) => setCountryCode(e.target.value)}
                                variant="standard"
                                disableUnderline
                                sx={{
                                  fontSize: 15,
                                  fontWeight: 500,
                                  color: "primary.main",
                                  minWidth: 70,
                                  mr: 1,
                                  "& .MuiSelect-icon": {
                                    color: "primary.main",
                                  },
                                }}
                              >
                                {COUNTRY_CODES.map((cc) => (
                                  <Mui.MenuItem key={cc.value} value={cc.value}>
                                    {cc.label}
                                  </Mui.MenuItem>
                                ))}
                              </Mui.Select>
                            </React.Fragment>
                          ) : identifierType === "email" ? (
                            <Mail color="primary" />
                          ) : (
                            <PersonOutlined color="primary" />
                          )}
                        </InputAdornment>
                      ),
                    }}
                  />

                  <Mui.Box sx={{ mt: 2.5, width: "100%" }}>
                    <Mui.Button
                      variant="contained"
                      type="submit"
                      size="large"
                      sx={primaryButtonSx}
                    >
                      {isLoading ? (
                        <Mui.CircularProgress size={24} color="inherit" />
                      ) : (
                        "Send reset code"
                      )}
                    </Mui.Button>
                  </Mui.Box>
                </Mui.Box>
              ) : (
                <Mui.Box sx={{ mt: 3.5, width: "100%" }}>
                  <Mui.Typography
                    sx={{
                      fontSize: 13,
                      letterSpacing: "0.07em",
                      textTransform: "uppercase",
                      fontWeight: 600,
                      color: "rgba(21,23,26,0.62)",
                      mb: 1.75,
                    }}
                  >
                    6-digit OTP
                  </Mui.Typography>

                  <Mui.Box
                    sx={{
                      display: "flex",
                      justifyContent: "center",
                      gap: 1.25,
                      flexWrap: { xs: "wrap", sm: "nowrap" },
                    }}
                  >
                    {otp.map((_, index) => (
                      <Mui.Box
                        key={index}
                        component="input"
                        ref={(el) => (inputRefs.current[index] = el)}
                        type="text"
                        maxLength="1"
                        value={otp[index]}
                        onChange={(e) => handleChange(e, index)}
                        onKeyDown={(e) => handleKeyDown(e, index)}
                        sx={{
                          width: { xs: "calc(16.666% - 10px)", sm: 50 },
                          minWidth: { xs: 0, sm: 50 },
                          height: 54,
                          borderRadius: "14px",
                          border: `1.5px solid ${
                            otp[index] === "" && otpVerification
                              ? themes.txtErrorColor
                              : tokens.inputBorder
                          }`,
                          textAlign: "center",
                          fontSize: 24,
                          outline: "none",
                          transition: "all 0.2s ease",
                          "&:focus": {
                            borderColor: tokens.primary,
                            boxShadow: tokens.focusRing,
                          },
                        }}
                      />
                    ))}
                  </Mui.Box>

                  <Mui.Box
                    sx={{
                      mt: 2,
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                    }}
                  >
                    <Mui.Typography
                      sx={{ color: "rgba(21,23,26,0.62)", fontSize: 14 }}
                    >
                      {isTimerActive
                        ? `Retry in ${secondsRemaining}s`
                        : "You can request a new code"}
                    </Mui.Typography>
                    {isTimerActive ? (
                      <Mui.Typography
                        sx={{ color: tokens.primary, fontSize: 14 }}
                      >
                        Resend code
                      </Mui.Typography>
                    ) : (
                      <Mui.Button
                        sx={{ textTransform: "none", fontSize: 14 }}
                        type="submit"
                      >
                        Resend code
                      </Mui.Button>
                    )}
                  </Mui.Box>

                  <Mui.Box
                    sx={{
                      mt: 3,
                      width: "100%",
                      borderRadius: 3,
                      background: "#f6f1e8",
                      px: 2.5,
                      py: 2,
                    }}
                  >
                    <Mui.Stack
                      direction="row"
                      spacing={1.5}
                      alignItems="flex-start"
                    >
                      <InfoOutlined sx={{ color: tokens.primary, mt: 0.5 }} />
                      <Mui.Typography
                        sx={{
                          fontSize: 14.5,
                          lineHeight: 1.5,
                          color: "rgba(21,23,26,0.8)",
                        }}
                      >
                        Code sent to your{" "}
                        {identifierType === "email"
                          ? "email address"
                          : "mobile number"}
                        . Check your{" "}
                        {identifierType === "email" ? "inbox" : "SMS"}.
                      </Mui.Typography>
                    </Mui.Stack>
                  </Mui.Box>

                  <Mui.Box sx={{ mt: 3.5, width: "100%" }}>
                    <Mui.Button
                      variant="contained"
                      type="button"
                      size="large"
                      sx={primaryButtonSx}
                      onClick={() => {
                        verifyOTP();
                      }}
                    >
                      {isLoading ? (
                        <Mui.CircularProgress size={24} color="inherit" />
                      ) : (
                        "Verify code"
                      )}
                    </Mui.Button>
                  </Mui.Box>
                </Mui.Box>
              )}
            </AuthCard>
          </form>
        </AuthRightPanel>
      </AuthLayout>
      <div id="recaptcha-container"></div>
    </React.Fragment>
  );
};
